import React, { useState } from 'react'
import styled from '@emotion/styled'
import { Outlet, NavLink as RouterNavLink } from 'react-router-dom'
import { buildRoute } from '../../../../pure-js/libs/RouteBuilder'
import RoutePath from '../../../../pure-js/libs/RoutePath'
import { usePortalAuth } from '../../hooks/usePortalAuth'
import { usePortalEnrollmentUser } from '../../hooks/usePortalEnrollmentUser'
import GrooverText from '../GrooverText'
import useIsMobile from '../../hooks/useIsMobile'
import { ProfileAvatar } from '../ProfileAvatar'
import { getPortalBrandingCssVars, usePortalBranding } from '../../hooks/usePortalBranding'
import { PortalViewAsBanner } from './PortalViewAsBanner'

const LayoutContainer = styled.div`
  display: flex;
  flex: 1;
  min-height: 0;
  width: 100%;
  max-width: 100%;
  min-width: 0;
  box-sizing: border-box;
  background: #f9fafb;
  position: relative;

  @media (max-width: 768px) {
    flex-direction: column;
    min-width: 0;
  }
`

const MobileHeader = styled.header`
  display: none;
  align-items: center;
  justify-content: space-between;
  padding: 16px 12px;
  width: 100%;
  box-sizing: border-box;
  background: #ffffff;
  border-bottom: 1px solid #e5e7eb;

  @media (max-width: 768px) {
    display: flex;
  }
`

const MenuButton = styled.button`
  padding: 8px;
  background: transparent;
  border: none;
  color: #1f2937;
  cursor: pointer;
  font-size: 24px;
`

const Logo = styled.div`
  font-size: 20px;
  font-weight: 700;
  color: var(--portal-primary, #7c3aed);
`

const LogoImage = styled.img`
  display: block;
  max-height: 32px;
  max-width: 180px;
  object-fit: contain;
`

const UserAvatar = ProfileAvatar

interface NavigationSidebarProps {
  isOpen: boolean
  onClose: () => void
}

const NavigationSidebar = styled.nav<NavigationSidebarProps>`
  width: 250px;
  flex-shrink: 0;
  background: #ffffff;
  border-right: 1px solid #e5e7eb;
  display: flex;
  flex-direction: column;
  padding: 24px 0;

  @media (max-width: 768px) {
    position: fixed;
    top: 0;
    left: 0;
    bottom: 0;
    z-index: 1000;
    transform: translateX(${(props) => (props.isOpen ? '0' : '-100%')});
    transition: transform 0.3s ease;
  }
`

const Overlay = styled.div<{ isOpen: boolean }>`
  display: none;

  @media (max-width: 768px) {
    display: ${(props) => (props.isOpen ? 'block' : 'none')};
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.5);
    z-index: 999;
  }
`

const NavHeader = styled.div`
  padding: 0 24px 24px;
  border-bottom: 1px solid #e5e7eb;
`

const NavLinks = styled.div`
  flex: 1;
  padding: 24px 0;
  display: flex;
  flex-direction: column;
  gap: 4px;
`

const NavLink = styled(RouterNavLink)<{ disabled?: boolean }>`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 24px;
  color: ${(props) => (props.disabled ? '#9ca3af' : '#6b7280')};
  text-decoration: none;
  font-size: 16px;
  font-weight: 500;
  transition: all 0.2s;
  cursor: ${(props) => (props.disabled ? 'not-allowed' : 'pointer')};
  pointer-events: ${(props) => (props.disabled ? 'none' : 'auto')};

  &:hover:not([disabled]) {
    background: rgba(var(--portal-primary-rgb, 124, 58, 237), 0.05);
    color: var(--portal-primary, #7c3aed);
  }

  &.active {
    background: rgba(var(--portal-primary-rgb, 124, 58, 237), 0.1);
    color: var(--portal-primary, #7c3aed);
    border-left: 3px solid var(--portal-primary, #7c3aed);
  }
`

const NavFooter = styled.div`
  padding: 16px 0 0;
  border-top: 1px solid #e5e7eb;
`

const UserInfoSection = styled.div`
  padding: 16px 24px;
  margin-bottom: 8px;
`

const UserInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`

const UserName = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: #111827;
`

const UserPhone = styled.div`
  font-size: 13px;
  color: #6b7280;
`

const MainContent = styled.main`
  flex: 1;
  min-width: 0;
  width: 100%;
  max-width: 100%;
  padding: 0;
  overflow-x: hidden;
  overflow-y: auto;
  background: #ffffff;
  box-sizing: border-box;

  /* Custom scrollbar styling */
  scrollbar-width: thin;
  scrollbar-color: #d1d5db #f9fafb;

  &::-webkit-scrollbar {
    width: 8px;
  }

  &::-webkit-scrollbar-track {
    background: #f9fafb;
  }

  &::-webkit-scrollbar-thumb {
    background: #d1d5db;
    border-radius: 4px;
  }

  &::-webkit-scrollbar-thumb:hover {
    background: #9ca3af;
  }

  @media (max-width: 768px) {
    padding: 0;
  }
`

export const PortalLayout: React.FC = () => {
  const { portalUser, portalClaimsReady } = usePortalAuth()
  const enrollmentUserId = portalClaimsReady ? (portalUser?.enrollmentUserId ?? null) : null
  const { enrollmentUser } = usePortalEnrollmentUser(enrollmentUserId)
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
  const isMobile = useIsMobile()
  const avatarLetter = portalUser?.name?.[0]?.toUpperCase() || 'U'
  const branding = usePortalBranding()

  const brandMark = branding.logoUrl ? (
    <LogoImage src={branding.logoUrl} alt={branding.name} />
  ) : (
    <Logo>{branding.name}</Logo>
  )

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        minHeight: '100vh',
        width: '100%',
        ...getPortalBrandingCssVars(branding)
      }}
    >
      <PortalViewAsBanner />
      <LayoutContainer>
        <MobileHeader>
          <MenuButton onClick={() => setMobileMenuOpen(!mobileMenuOpen)}>☰</MenuButton>
          {brandMark}
          <UserAvatar profileImagePath={enrollmentUser?.profileImagePath} fallbackLetter={avatarLetter} size={32} />
        </MobileHeader>

        {/* Overlay for mobile */}
        <Overlay isOpen={mobileMenuOpen} onClick={() => setMobileMenuOpen(false)} />

        {/* Navigation Sidebar */}
        <NavigationSidebar isOpen={!isMobile || mobileMenuOpen} onClose={() => setMobileMenuOpen(false)}>
          <NavHeader>{brandMark}</NavHeader>

          <NavLinks>
            <NavLink to={buildRoute(RoutePath.PORTAL_DASHBOARD)} onClick={() => setMobileMenuOpen(false)}>
              <GrooverText textKey="portalNavHome" />
            </NavLink>
            <NavLink to={buildRoute(RoutePath.PORTAL_ENROLLMENTS)} onClick={() => setMobileMenuOpen(false)}>
              <GrooverText textKey="portalEnrollments" />
            </NavLink>
            <NavLink to={buildRoute(RoutePath.PORTAL_HOME_ORDERS)} onClick={() => setMobileMenuOpen(false)}>
              <GrooverText textKey="portalPrivateLessons" />
            </NavLink>
            <NavLink to={buildRoute(RoutePath.PORTAL_DROPIN_TICKETS)} onClick={() => setMobileMenuOpen(false)}>
              <GrooverText textKey="portalDropinTickets" />
            </NavLink>
            <NavLink to={buildRoute(RoutePath.PORTAL_WALKIN_TICKETS)} onClick={() => setMobileMenuOpen(false)}>
              <GrooverText textKey="portalWalkinTickets" />
            </NavLink>
            <NavLink to={buildRoute(RoutePath.PORTAL_PROFILE)} onClick={() => setMobileMenuOpen(false)}>
              <GrooverText textKey="portalNavProfile" />
            </NavLink>
          </NavLinks>

          <NavFooter>
            <UserInfoSection>
              <UserInfo>
                <UserName>{portalUser?.name || 'User'}</UserName>
                <UserPhone>{portalUser?.phone}</UserPhone>
              </UserInfo>
            </UserInfoSection>
            <NavLink to={buildRoute(RoutePath.PORTAL_LOGOUT)} onClick={() => setMobileMenuOpen(false)}>
              <GrooverText textKey="portalLogout" />
            </NavLink>
          </NavFooter>
        </NavigationSidebar>

        {/* Main Content Area */}
        <MainContent>
          <Outlet />
        </MainContent>
      </LayoutContainer>
    </div>
  )
}
